import { FC } from 'react';

import {
  AvatarGroup,
  Box,
  Flex,
  Tag,
  Text,
  useColorMode,
} from '@chakra-ui/react';
import { AiOutlineInfoCircle } from 'react-icons/ai';

import { xTokenMapping } from '@/constants/constants';
import { defiCurrencyIconMapping } from '@/constants/dappWalletMapping';
// Constants
import { PartnerTypes } from '@/constants/defi';
// Types
import { DefiItemActionData } from '@/customtypes/global';
import BaseImage from '@/dapp-sdk-v2/components/Image';
// Components
import { TextPrimary } from '@/themes';

import Actions from './Actions';
import { NormalizedDefiItem } from './defi-page.types';
import { renderAvatarwithSVG } from './helpers';

// Images
import sdIcon from '@/assets/images/sd.png';

const xToken = process.env.NEXT_PUBLIC_XTOKEN || '';

interface PropTypes {
  defiItem: NormalizedDefiItem;
  links: DefiItemActionData[];
  APY?: string | number;
  TVL?: string;
  hasAPY?: boolean;
  isSDPool?: boolean;
  tags?: string[];
  paused?: boolean;
  itemData: any;
}

const Item: FC<PropTypes> = ({
  defiItem,
  links,
  APY,
  TVL,
  hasAPY,
  tags,
  isSDPool,
  paused,
}: PropTypes) => {
  const { colorMode } = useColorMode();

  const { type, token } = defiItem;

  const isVault = type === PartnerTypes.VAULT;

  const getTokenTitle = (type: string) => {
    if (
      type === PartnerTypes.LENDING_BORROWING ||
      type === PartnerTypes.OPTION_TADING
    ) {
      return `${isSDPool ? 'SD' : xTokenMapping[xToken]}`;
    }
    return `${isSDPool ? 'SD' : xTokenMapping[xToken]}${
      defiItem.showToken !== false && token ? ` / ${token}` : ''
    }`;
  };

  return (
    <Flex
      justify="space-between"
      align={{ base: undefined, md: 'center' }}
      flexDir={{ base: 'column', md: 'row' }}
      gap={{ base: '6', md: undefined }}
    >
      <Flex flex="1" gap={{ base: '0', lg: '5rem' }}>
        <Flex gap="4" align="center" flex="1">
          {type === PartnerTypes.LENDING_BORROWING ||
          (type === PartnerTypes.DEX && defiItem.showToken === false) ? (
            <Box w={{ base: 'fit-content', xl: '48px' }}>
              {renderAvatarwithSVG(
                isSDPool
                  ? sdIcon
                  : defiCurrencyIconMapping[xToken.toLowerCase()][colorMode],
                isSDPool ? 'SD' : xTokenMapping[xToken],
              )}
            </Box>
          ) : (
            <>
              <AvatarGroup spacing="-1" size="xs">
                {!isVault &&
                  renderAvatarwithSVG(
                    isSDPool
                      ? sdIcon
                      : defiCurrencyIconMapping[xToken.toLowerCase()][
                          colorMode
                        ],
                    isSDPool ? 'SD' : xTokenMapping[xToken],
                  )}
                {defiItem?.tokenIconKey &&
                  renderAvatarwithSVG(
                    defiCurrencyIconMapping[
                      defiItem?.tokenIconKey?.toLowerCase() || ''
                    ]?.[colorMode],
                    'token',
                  )}
              </AvatarGroup>
            </>
          )}
          <Box>
            {isVault ? (
              <TextPrimary fontSize="1rem" fontWeight="600">
                {token}
              </TextPrimary>
            ) : (
              <TextPrimary fontSize="1rem" fontWeight="600">
                {getTokenTitle(type)}
              </TextPrimary>
            )}
            {tags?.map((tag) => (
              <Tag
                key={tag}
                size="sm"
                bg={tag === 'NEW' ? '#EBF8FF' : '#F0FFF4'}
                color={tag === 'NEW' ? '#3182CE' : '#38A169'}
                mt="1"
                fontWeight="700"
                fontSize="10px"
              >
                {tag}
              </Tag>
            ))}
          </Box>
        </Flex>

        <Flex align="center" display={{ base: 'inherit', lg: 'none' }} gap={2}>
          <AvatarGroup spacing="0.5" size="xs">
            {defiItem.pairPlatform && defiItem?.pairPlatform?.icon_key && (
              <BaseImage
                height={24}
                width={24}
                src={defiItem.pairPlatform.icon_key}
                alt={defiItem?.platform?.title || 'platform'}
              />
            )}
            {defiItem?.platform?.icon_key && (
              <BaseImage
                height={24}
                width={24}
                src={defiItem.platform.icon_key}
                alt={defiItem?.platform?.title || 'platform'}
              />
            )}
          </AvatarGroup>
        </Flex>
      </Flex>

      <Flex
        flex="1"
        justify={{ base: 'space-between', md: 'flex-start' }}
        align="center"
      >
        <TextPrimary fontSize="1rem" fontWeight="600" maxW="160px">
          {hasAPY ? 'APY:' : 'APR:'}
          <Flex align="center" display="inline-flex">
            <Text ml=".5rem">{APY ? `${APY} %` : '-'}</Text>
            {defiItem.apyMessage && (
              <Text ml=".5rem" color="textSecondary" fontSize="14px">
                <AiOutlineInfoCircle />
              </Text>
            )}
          </Flex>
        </TextPrimary>

        <TextPrimary fontSize="1rem" fontWeight="600">
          {type === PartnerTypes.LEVERAGE_TADING ? 'VOL:' : 'TVL:'}
          <Text as="span" ml=".5rem">
            {TVL || '-'}
          </Text>
        </TextPrimary>
      </Flex>

      <Actions
        id={defiItem.apiKey}
        type={type}
        links={links}
        isPaused={paused as boolean}
      />
    </Flex>
  );
};

export default Item;
